import { Link, useLocation } from "react-router-dom";
import { lazy, Suspense } from "react";
import type { ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { Menu, MenuItem, MenuSeparator } from "./ui/menu";
import { BrandMark } from "./BrandMark";
import { ErrorBoundary } from "./ErrorBoundary";
import { WorkspaceSwitcher } from "./workspace/WorkspaceSwitcher";
import { logout } from "@/lib/auth/logout";
import { useSession } from "@/lib/auth/useSession";
import type { MeUser } from "@/lib/auth/types";
import {
  createWorkspaceLinks,
  workspaceSlugFromPath,
} from "@/lib/workspace/paths";
import { ActivityControllerProvider } from "./activity/controller";

const ActivityBoundary = lazy(() =>
  import("./activity/ActivityBoundary").then(({ ActivityBoundary }) => ({
    default: ActivityBoundary,
  })),
);

/**
 * Three zones and no tab row: the mark, the organization, the account.
 *
 * There is exactly one content destination — the characters listing — and a
 * one-tab tab bar is furniture rather than navigation. Organization
 * administration hangs off the organization it configures, in the switcher
 * menu, not off a global nav that implies it is somewhere else to work.
 *
 * The URL names the active organization from the viewer's real memberships,
 * so a restored tab and a pasted link agree without any client-side "current
 * organization" to go stale.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { data: session } = useSession();
  const slug = workspaceSlugFromPath(pathname);
  const active = session?.orgs.find((workspace) => workspace.slug === slug);
  const links = active ? createWorkspaceLinks(active.slug) : null;
  // Activity is content: an organization without its own Characters workspace
  // would answer every activity request with not-provisioned, so it gets no
  // activity scope and no boundary at all.
  const content = active?.characters_workspace ? active : undefined;
  const activityScope =
    session && content ? `${session.user.id}:${content.slug}` : "";

  return (
    <ActivityControllerProvider scope={activityScope}>
      <div className="flex min-h-dvh flex-col bg-bg text-fg">
        <header
          className="sticky top-0 z-20 border-b border-border bg-bg/95 backdrop-blur motion-reduce:backdrop-blur-none"
          style={{ paddingTop: "env(safe-area-inset-top)" }}
        >
          <div className="mx-auto flex min-h-16 w-full max-w-6xl items-center justify-between gap-3 px-4 py-2 sm:px-6">
            <div className="flex min-w-0 items-center gap-2">
              <Link
                to={links ? links.characters : "/"}
                className="flex min-w-0 items-center gap-2.5 rounded-md font-bold tracking-tight"
              >
                <BrandMark />
                <span className="hidden truncate text-base sm:inline sm:text-lg">
                  Character Studio
                </span>
              </Link>
              {/* Signed out, there are no workspaces to choose between, and a
                  switcher offering "Choose a workspace" on the login screen reads
                  as an account problem rather than as a missing session. */}
              {session && <WorkspaceSwitcher active={active} />}
            </div>
            {session && (
              <div className="flex shrink-0 items-center gap-1">
                {content && links && (
                  <ErrorBoundary
                    key={`${session.user.id}:${content.slug}`}
                    fallback={() => null}
                  >
                    <Suspense fallback={null}>
                      <ActivityBoundary
                        key={`${session.user.id}:${content.slug}`}
                        userID={session.user.id}
                        orgSlug={content.slug}
                        orgName={content.display_name}
                        links={links}
                      />
                    </Suspense>
                  </ErrorBoundary>
                )}
                <AccountMenu user={session.user} />
              </div>
            )}
          </div>
        </header>

        <main
          className="mx-auto w-full max-w-6xl flex-1"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          {children}
        </main>
      </div>
    </ActivityControllerProvider>
  );
}

/**
 * The first letters of the name, or of the address when there is no name.
 *
 * Google omits a picture for accounts that never set one, and an empty avatar
 * slot in the one permanently visible control reads as a broken image rather
 * than as a missing preference.
 */
function initials(user: MeUser): string {
  const source = user.name.trim() || user.email;
  const parts = source.split(/[\s@._-]+/).filter((part) => part !== "");
  if (parts.length === 0) return "?";
  return parts
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");
}

function AccountMenu({ user }: { user: MeUser }) {
  return (
    <Menu
      label="Account"
      align="end"
      trigger={
        <>
          {user.avatar_url ? (
            <img
              src={user.avatar_url}
              alt=""
              className="size-7 shrink-0 rounded-pill"
              referrerPolicy="no-referrer"
            />
          ) : (
            <span
              aria-hidden="true"
              className="flex size-7 shrink-0 items-center justify-center rounded-pill bg-bg-sunken text-[11px] font-semibold text-fg-muted"
            >
              {initials(user)}
            </span>
          )}
          <ChevronDown className="size-4 text-fg-subtle" aria-hidden="true" />
        </>
      }
    >
      <div className="px-2.5 py-2">
        <p className="truncate text-sm font-medium">
          {user.name || user.email}
        </p>
        <p className="truncate text-[11px] text-fg-subtle">{user.email}</p>
      </div>
      <MenuSeparator />
      {/* Signing out is a once-a-day action; as a permanent header button it
          held real estate one mis-click from everything else. */}
      <MenuItem
        onSelect={() => {
          void logout();
        }}
      >
        Sign out
      </MenuItem>
    </Menu>
  );
}
